import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Divider,
  Grid,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import sanitizeHtml from 'sanitize-html';
import { capitalize } from 'lodash';
import useUserInfo from '@src/hooks/useTtsUser';
import moment from 'moment';
import { delimitNumber, formatNumber } from '@src/utils/number';
import { useLocation } from 'react-router-dom';
import theme from '@src/styles/theme';
import { LANGUAGE } from '@src/constants';
import { BREAKPOINTS } from '@src/styles/config';
import { checkVietNam } from '@src/utils/checkCountry';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Tooltip from '@src/components/Tooltip';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import camelcaseKeys from 'camelcase-keys';
import {
  PACK_DURATION,
  PACKAGE_CHANGE_TYPES,
  PACKAGE_STUDIO_VERSION,
  PACKAGE_TYPE,
  TOP_UP_PACKAGE_CODES,
} from '@src/constants/package';
import { getPriceText } from '@src/services/payment';
import {
  calculateDaysInCurrentCycle,
  calculateResourcePrice,
  calculateTimeToExpiry,
  checkCustomPackage,
  formatPrice,
  getDaysRemaining,
  getPackageChangeType,
  getPackageCurrentPrice,
  getPackageName,
  getPackageNameV3,
  getRemainingCycles,
  roundSaleOffPercentage,
} from '@src/services/package';
import { getSavedPrice } from '@src/services/voucher';
import NotificationDialogV3 from '@src/components/NotificationDialogV3';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { StyledOrder } from './index.style';

const Order = ({
  remainingValueOfTheCurrentPackage,
  calculateCharacterPurchase,
  currentNumberCharacters,
  customPackagePrice,
  orderPackageInfoWhenApplyVoucher,
  creditsOrderData,
  loadingGetBanks,
  loadingGetPreviewRolloverCredits,
}) => {
  const { getFeatureValue } = useFeatureFlags();
  const isMobile = useMediaQuery(theme.breakpoints.down(BREAKPOINTS.SMD));
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const { ttsUser } = useUserInfo();
  const { voucher: voucherState, package: packageState } = useSelector(
    (state) => state.order,
  );
  const { customResource } = packageState || {};
  const { usingPackage } = useSelector((state) => state.user);
  const { user } = useSelector((state) => state.auth);
  const [openPopupContentPricesResources, setOpenPopupContentPricesResources] =
    useState(false);
  const [openPopupInfoTooltipMobile, setOpenPopupInfoTooltipMobile] =
    useState(false);
  const isVietNam = checkVietNam();
  const packageName = getPackageNameV3(
    usingPackage?.name,
    usingPackage?.code,
    language,
    t,
  );
  const useRenameMergeProduct = getFeatureValue(
    FEATURE_KEYS.RENAME_MERGE_PRODUCT,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
    },
  );
  const isRolloverCredits = getFeatureValue(FEATURE_KEYS.ROLLOVER_CREDITS, {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  });

  const isUsingCustomPackage = checkCustomPackage({
    type: usingPackage.type,
    level: usingPackage.level,
  });

  const remainingCredits = isUsingCustomPackage
    ? ttsUser?.studio?.custom?.remainingCredits || 0
    : (ttsUser?.studio?.cycle?.remainingCredits || 0) +
      (ttsUser?.studio?.topUp?.remainingCredits || 0);

  const { price, usdPrice } = getPackageCurrentPrice(packageState, true);
  const currentProduct = useMemo(() => {
    const packageTypes = [
      {
        label: useRenameMergeProduct ? t('AIVOICE_STUDIO') : t('studioPackage'),
        id: PACKAGE_TYPE.STUDIO,
      },
      { label: t('DUBBING'), id: PACKAGE_TYPE.DUBBING },
    ];
    const selectLabel = packageTypes?.find(
      (item) => item?.id === packageState?.type,
    )?.label;
    return selectLabel;
  }, [packageState, language]);

  const {
    package: packageCode,
    credits: creditsQuery,
    devices: devicesQuery,
    voiceClonings: voiceCloningsQuery,
  } = camelcaseKeys(queryString.parse(location.search) || {});

  const isUIBuyTopupCharacters = TOP_UP_PACKAGE_CODES.includes(packageCode);
  const isCustomPackage = checkCustomPackage({
    type: packageState.type,
    level: packageState.level,
  });

  const totalPrice = useMemo(() => {
    if (isUIBuyTopupCharacters) {
      const topUpCreditsPrice = isVietNam
        ? calculateCharacterPurchase?.packagePrice
        : calculateCharacterPurchase?.usdPackagePrice;
      return topUpCreditsPrice;
    }

    let packagePrice = isVietNam
      ? price || packageState.price
      : usdPrice || packageState.usdPrice;

    if (isCustomPackage) {
      packagePrice = customPackagePrice?.price;
    }

    return packagePrice;
  }, [
    customPackagePrice,
    isCustomPackage,
    isVietNam,
    packageState,
    usdPrice,
    price,
    calculateCharacterPurchase,
    isUIBuyTopupCharacters,
  ]);

  const remainingDate = useMemo(() => {
    if (isUsingCustomPackage) {
      const remainingDays = calculateTimeToExpiry(ttsUser?.packageExpiryDate);
      if (remainingDays === 0) return `${t('today')}`;
      return `${remainingDays} ${t('days')}`;
    }

    let remainingCycles = getRemainingCycles(
      user.packageExpiryDate,
      ttsUser?.studio?.cycle?.nextResetAt,
    );

    const daysInCycle = calculateDaysInCurrentCycle(user.packageStartDate);
    let remainingDays = getDaysRemaining(ttsUser?.studio?.cycle?.nextResetAt);

    if (!isUIBuyTopupCharacters) {
      if (remainingDays >= daysInCycle) {
        remainingCycles += 1;
        remainingDays = 0;
      }

      let result = '';
      if (remainingCycles > 0) result += `${remainingCycles} ${t('months')} `;
      if (remainingDays > 0 || remainingCycles === 0)
        result += `${remainingDays} ${t('days')}`;

      return result.trim();
    }
    return '';
  }, [user, t, isUsingCustomPackage]);

  const determinePackageChange = () => {
    if (
      !usingPackage ||
      !usingPackage?.version?.includes(PACKAGE_STUDIO_VERSION.V3)
    )
      return null;
    return getPackageChangeType({
      currPackage: packageState,
      prevPackage: usingPackage,
      prevPackageExpiredDate: ttsUser?.packageExpiryDate,
    });
  };

  const formatExpDate = (date) =>
    moment(date).endOf('day').format('DD/MM/YYYY');

  const formatExpDateWithTime = (date) =>
    moment(date).endOf('day').format('DD/MM/YYYY - HH:mm');

  const getExpDate = () => {
    if (isUIBuyTopupCharacters)
      return formatExpDateWithTime(ttsUser?.studio?.cycle?.nextResetAt);

    if (isCustomPackage)
      return formatExpDate(moment().add(1, 'year').toISOString());

    return formatExpDate(
      moment()
        .add(orderPackageInfoWhenApplyVoucher?.cycle?.totalCycles, 'months')
        .toISOString(),
    );
  };

  const getDiscountPercentageCredits = (prices) => {
    const actualPrice = isVietNam ? prices.vndPrice : prices.usdPrice;
    const originalPrice = isVietNam
      ? prices.originalVndPrice
      : prices.originalUsdPrice;
    const discountPercent = (1 - actualPrice / originalPrice) * 100;

    return roundSaleOffPercentage(discountPercent);
  };

  // ============================== Popup Tooltip ==============================

  const contentTooltipResources = () => {
    const { credits, devices, voiceClonings } = customResource || {};
    const prices = {
      credits: calculateResourcePrice({
        value: +creditsQuery || credits?.min,
        pricingPlans: credits,
      }),
      devices: calculateResourcePrice({
        value: +devicesQuery || 0,
        pricingPlans: devices,
      }),
      voiceClonings: calculateResourcePrice({
        value: +voiceCloningsQuery || 0,
        pricingPlans: voiceClonings,
      }),
    };

    const contentTooltipItem = (
      priceResource,
      translationKey,
      options = {},
    ) => {
      if (!priceResource?.vndPrice) {
        return null;
      }

      const pluralMapping = {
        devicesPrice: {
          singular: 'device',
          plural: 'devices',
          count: options?.amountDevices,
        },
        voiceCloningPrice: {
          singular: 'voice',
          plural: 'voices',
          count: options?.amountVoiceCloning,
        },
      };

      const mapping = pluralMapping?.[translationKey];
      const pluralForm =
        mapping?.count > 1 ? mapping?.plural : mapping?.singular;

      const formattedPrice = formatPrice({
        vndPrice: priceResource.vndPrice,
        usdPrice: priceResource.usdPrice,
        isVietNam,
        language,
      });

      const translatedText = t(translationKey, {
        price: formattedPrice,
        ...options,
      });

      const contentTooltip = sanitizeHtml(
        mapping
          ? translatedText.replace(mapping.plural, pluralForm)
          : translatedText,
        {
          allowedTags: ['span'],
          allowedAttributes: {
            span: ['style'],
          },
        },
      );

      return (
        <li style={{ textAlign: 'left' }}>
          <span
            style={{ width: 'max-content', marginBottom: '4px' }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: contentTooltip }}
          />
          {!!options.saledOffPercent && (
            <span>
              {t('saledOffPercent', {
                saleOff: options.saledOffPercent,
              })}
            </span>
          )}
        </li>
      );
    };

    return (
      <ul style={{ margin: 0, padding: '8px', fontSize: '14px' }}>
        {contentTooltipItem(prices.credits, 'creditsPrice', {
          saledOffPercent: getDiscountPercentageCredits(prices.credits),
        })}
        {contentTooltipItem(prices.devices, 'devicesPrice', {
          amountDevices: devicesQuery,
        })}
        {contentTooltipItem(prices.voiceClonings, 'voiceCloningPrice', {
          amountVoiceCloning: voiceCloningsQuery,
        })}
      </ul>
    );
  };

  const contentTooltipResourcesRollover = () => {
    const { credits, devices, voiceClonings } = customResource || {};

    const currentAdditionalDevices = Math.max(
      0,
      user.maxDevices - devices?.min,
    );
    const currentAdditionalVoice = Math.max(
      0,
      user.maxCloningVoices - voiceClonings?.min,
    );

    const devicesCount = Math.max(0, +devicesQuery - currentAdditionalDevices);
    const voiceCount = Math.max(
      0,
      +voiceCloningsQuery - currentAdditionalVoice,
    );

    const prices = {
      credits: calculateResourcePrice({
        value: +creditsQuery || credits?.min,
        pricingPlans: credits,
      }),
      devices: calculateResourcePrice({
        value: +devicesCount,
        pricingPlans: devices,
      }),
      voiceClonings: calculateResourcePrice({
        value: +voiceCount,
        pricingPlans: voiceClonings,
      }),
    };

    const contentTooltipItem = (
      priceResource,
      translationKey,
      options = {},
    ) => {
      const pluralMapping = {
        creditsPrice: {
          singular: 'credit',
          plural: 'credits',
          count: options?.amount,
        },
        additionalDevicesFee: {
          singular: 'device',
          plural: 'devices',
          count: options?.amount,
        },
        additionalVoicesFee: {
          singular: 'voice',
          plural: 'voices',
          count: options?.amount,
        },
      };

      const mapping = pluralMapping?.[translationKey];
      const pluralForm =
        mapping?.count > 1 ? mapping?.plural : mapping?.singular;

      const formattedPrice = formatPrice({
        vndPrice: priceResource.vndPrice,
        usdPrice: priceResource.usdPrice,
        isVietNam,
        language,
      });

      const translatedText = t(translationKey, {
        price: formattedPrice,
        ...options,
      });

      const contentTooltip = sanitizeHtml(
        mapping
          ? translatedText.replace(mapping.plural, pluralForm)
          : translatedText,
        {
          allowedTags: ['span'],
          allowedAttributes: {
            span: ['style'],
          },
        },
      );

      return (
        <li style={{ textAlign: 'left' }}>
          <span
            style={{ width: 'max-content', marginBottom: '4px' }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: contentTooltip }}
          />
          {!!options.saledOffPercent && (
            <span>
              {t('saledOffPercent', {
                saleOff: options.saledOffPercent,
              })}
            </span>
          )}
        </li>
      );
    };

    return (
      <ul
        style={{
          margin: 0,
          padding: '8px',
          fontSize: '14px',
          width: 'max-content',
        }}
      >
        {contentTooltipItem(prices.credits, 'creditsPrice', {
          saledOffPercent: getDiscountPercentageCredits(prices.credits),
        })}
        {contentTooltipItem(prices.devices, 'additionalDevicesFee', {
          amount: Math.max(0, devicesCount),
        })}
        {contentTooltipItem(prices.voiceClonings, 'additionalVoicesFee', {
          amount: Math.max(0, voiceCount),
        })}
      </ul>
    );
  };

  const contentTooltipRollover = ({ credits, rolloverCredits }) => {
    const contentTooltipItem = (key) => {
      const translatedText = t(key, {
        credits: Number(credits)?.toLocaleString(language),
        rolloverCredits: Number(rolloverCredits)?.toLocaleString(language),
      });

      const contentTooltip = sanitizeHtml(translatedText, {
        allowedTags: ['span'],
        allowedAttributes: {
          span: ['style'],
        },
      });

      return (
        <li style={{ textAlign: 'left' }}>
          <span
            style={{ width: 'max-content', marginBottom: '4px' }}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: contentTooltip }}
          />
        </li>
      );
    };

    return (
      <ul style={{ margin: 0, padding: '8px', fontSize: '14px' }}>
        {!!credits && contentTooltipItem('newPackageCharacters')}
        {!!rolloverCredits && contentTooltipItem('rolloverCharactersPackage')}
      </ul>
    );
  };

  const PopupContentPricesResources = () => (
    <NotificationDialogV3
      name={t('detailedInformation')}
      title={t('detailedInformation')}
      description={
        isRolloverCredits && creditsOrderData?.isShowTooltipRolloverCredits
          ? contentTooltipResourcesRollover()
          : contentTooltipResources()
      }
      open={openPopupContentPricesResources && isMobile}
      onClose={() => setOpenPopupContentPricesResources(false)}
      variant="null"
      fitWidth
      hideDangerouslySetInnerHTML
      actionComponent={
        <Button
          onClick={() => setOpenPopupContentPricesResources(false)}
          className="btn-close-popup"
          variant="contained"
        >
          {t('gotIt')}
        </Button>
      }
    />
  );

  const contentTooltipAutoRenew = () => {
    const priceRenew = isVietNam ? price : usdPrice;

    const nextPaymentDate = () => {
      let expDate = moment().toISOString();

      if (packageState?.expiresIn === PACK_DURATION?.YEAR)
        expDate = moment().add(1, 'year').toISOString();
      if (packageState?.expiresIn === PACK_DURATION?.MONTH)
        expDate = moment().add(1, 'month').toISOString();

      return moment(expDate).format('DD/MM/YYYY');
    };
    return (
      <Typography
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: t('autoRenewContentTooltip', {
            priceRenew: getPriceText(priceRenew, isVietNam, language),
            nextPaymentDate: nextPaymentDate(),
          }),
        }}
      />
    );
  };

  const contentTooltipInfoPriceAutoRenew = () => (
    <Typography
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: t('subscriptionInfoNote'),
      }}
    />
  );

  const PopupInfoTooltipMobile = () => (
    <NotificationDialogV3
      name={t('detailInfo')}
      title={t('detailInfo')}
      description={openPopupInfoTooltipMobile}
      hideDangerouslySetInnerHTML
      open={!!openPopupInfoTooltipMobile && isMobile}
      onClose={() => setOpenPopupInfoTooltipMobile(false)}
      variant="null"
      fitWidth
      actionComponent={
        <Button
          onClick={() => setOpenPopupInfoTooltipMobile(false)}
          className="btn-close-popup"
          variant="contained"
        >
          {t('gotIt')}
        </Button>
      }
    />
  );
  // ==============================

  const TotalCredits = () => {
    if (packageState?.cycle) return null;

    const creditsCustomPackage =
      orderPackageInfoWhenApplyVoucher?.maxCredits ||
      creditsQuery ||
      customResource?.credits?.min;

    const renderCreditsTitle = () => {
      if (isCustomPackage) {
        return (
          <div className="details-price-resources">
            {t('totalCreditsTitle')}
            {creditsOrderData?.isRolloverCredits && (
              <Tooltip
                arrow
                placement="top"
                title={contentTooltipRollover({
                  credits: creditsCustomPackage,
                  rolloverCredits: creditsOrderData?.creditsRollover,
                })}
                onClick={() =>
                  setOpenPopupInfoTooltipMobile(
                    contentTooltipRollover({
                      credits: creditsCustomPackage,
                      rolloverCredits: creditsOrderData?.creditsRollover,
                    }),
                  )
                }
                maxWidth="100%"
              >
                <InfoRoundedIcon
                  fontSize="16px"
                  className="icon-info-rounded"
                />
              </Tooltip>
            )}
          </div>
        );
      }

      return (
        <Typography className="package-info-title">
          {isUIBuyTopupCharacters
            ? t('numberCharacters')
            : capitalize(t('character'))}
        </Typography>
      );
    };

    const renderCreditsDetails = () => {
      if (isUIBuyTopupCharacters) {
        return (
          // TODO: use orderPackageInfoWhenApplyVoucher when we have voucher for topup package
          <Typography>
            {t('remainCharacters', {
              numberCharacters: delimitNumber(
                currentNumberCharacters,
                language,
              ),
            })}
          </Typography>
        );
      }

      if (!isUIBuyTopupCharacters || isCustomPackage) {
        const credits = isCustomPackage
          ? +creditsCustomPackage +
            (creditsOrderData?.creditsCustomPackage || 0)
          : orderPackageInfoWhenApplyVoucher?.maxCharacters;

        return (
          <Typography>
            {formatNumber(credits, language)} {t('characters').toLowerCase()}
          </Typography>
        );
      }

      return null;
    };

    return (
      <Box className="package-info-card">
        {renderCreditsTitle()}
        <Box className="package-info-detail">{renderCreditsDetails()}</Box>
      </Box>
    );
  };

  const TimeExpire = () => {
    const amountCycle = isCustomPackage
      ? Math.round(orderPackageInfoWhenApplyVoucher?.expiresIn / 30)
      : orderPackageInfoWhenApplyVoucher?.cycle?.totalCycles;
    const labelCycle = amountCycle > 1 ? 'months' : 'month';
    const isSubscription =
      orderPackageInfoWhenApplyVoucher?.cycle || isCustomPackage;
    if (isSubscription && !isUIBuyTopupCharacters) {
      return (
        <Box className="package-info-card">
          <Box className="package-info-detail">
            {`${amountCycle} ${t(labelCycle)}`} - {getExpDate()}
          </Box>
        </Box>
      );
    }
    if (isUIBuyTopupCharacters) {
      return (
        <Box className="package-info-card">
          <Box className="package-info-detail">{getExpDate()}</Box>
        </Box>
      );
    }
    return null;
  };

  const TotalCharsPerMonth = () => {
    // const isPackageV3Year =
    //   packageState?.cycle && packageState?.expiresIn !== PACK_DURATION.MONTH;

    if (!isUIBuyTopupCharacters && !isCustomPackage) {
      return (
        <Box className="package-info-card">
          <Typography className="package-info-title">
            {t('charsPerMonth')}
          </Typography>
          <Box className="package-info-detail lower-case-text">
            {orderPackageInfoWhenApplyVoucher?.cycle?.creditsPerCycle?.toLocaleString(
              language,
            )}{' '}
            {t('manycharacters')}
          </Box>
        </Box>
      );
    }
    return null;
  };

  const RolloverCredits = () => {
    const totalCycle = orderPackageInfoWhenApplyVoucher?.cycle?.totalCycles;
    const isEqualCredits =
      creditsOrderData?.creditsFirstMonth ===
      creditsOrderData?.creditsEachMonth;

    if (isEqualCredits) {
      return (
        <Box className="package-info-card">
          <div className="details-price-resources">
            {t('charsPerMonth')}
            <Tooltip
              arrow
              placement="top"
              title={contentTooltipRollover({
                credits: creditsOrderData?.creditsPerCycle,
                rolloverCredits: creditsOrderData?.creditsRollover,
              })}
              onClick={() =>
                setOpenPopupInfoTooltipMobile(
                  contentTooltipRollover({
                    credits: creditsOrderData?.creditsPerCycle,
                    rolloverCredits: creditsOrderData?.creditsRollover,
                  }),
                )
              }
            >
              <InfoRoundedIcon fontSize="16px" className="icon-info-rounded" />
            </Tooltip>
          </div>
          <Box className="package-info-detail">
            {' '}
            <Typography>
              {(
                orderPackageInfoWhenApplyVoucher?.cycle?.creditsPerCycle +
                creditsOrderData?.creditsRollover
              )?.toLocaleString(language)}{' '}
              {t('manycharacters')}
            </Typography>
          </Box>
        </Box>
      );
    }
    if (totalCycle > 1)
      return (
        <>
          <Box className="package-info-card">
            <Typography className="package-info-title">
              <div className="details-price-resources">
                {t('firstMonth')}
                <Tooltip
                  arrow
                  title={contentTooltipRollover({
                    credits: creditsOrderData?.creditsPerCycle,
                    rolloverCredits: creditsOrderData?.creditsRollover,
                  })}
                  placement="top"
                  onClick={() =>
                    setOpenPopupInfoTooltipMobile(
                      contentTooltipRollover({
                        credits: creditsOrderData?.creditsPerCycle,
                        rolloverCredits: creditsOrderData?.creditsRollover,
                      }),
                    )
                  }
                >
                  <InfoRoundedIcon
                    fontSize="16px"
                    className="icon-info-rounded"
                  />
                </Tooltip>
              </div>
            </Typography>
            <Box className="package-info-detail">
              {creditsOrderData?.creditsFirstMonth?.toLocaleString(language)}{' '}
              {t('characterNew')}
            </Box>
          </Box>
          <Box className="package-info-card">
            <Typography className="package-info-title">
              <div className="details-price-resources">
                {t('eachMonthThereafter')}
                <Tooltip
                  title={contentTooltipRollover({
                    credits: creditsOrderData?.creditsPerCycle,
                    rolloverCredits: creditsOrderData?.cycleCreditsPerCycle,
                  })}
                  arrow
                  placement="top"
                  onClick={() =>
                    setOpenPopupInfoTooltipMobile(
                      contentTooltipRollover({
                        credits: creditsOrderData?.creditsPerCycle,
                        rolloverCredits: creditsOrderData?.cycleCreditsPerCycle,
                      }),
                    )
                  }
                >
                  <InfoRoundedIcon
                    fontSize="16px"
                    className="icon-info-rounded"
                  />
                </Tooltip>
              </div>
            </Typography>
            <Box className="package-info-detail">
              {creditsOrderData?.creditsEachMonth?.toLocaleString(language)}{' '}
              {t('characterNew')}
            </Box>
          </Box>
        </>
      );
    return (
      <Box className="package-info-card">
        <div className="details-price-resources">
          {t('totalCreditsTitle')}
          <Tooltip
            arrow
            placement="top"
            title={contentTooltipRollover({
              credits: creditsOrderData?.creditsPerCycle,
              rolloverCredits: creditsOrderData?.creditsRollover,
            })}
            onClick={() =>
              setOpenPopupInfoTooltipMobile(
                contentTooltipRollover({
                  credits: creditsOrderData?.creditsPerCycle,
                  rolloverCredits: creditsOrderData?.creditsRollover,
                }),
              )
            }
          >
            <InfoRoundedIcon fontSize="16px" className="icon-info-rounded" />
          </Tooltip>
        </div>
        <Box className="package-info-detail">
          {' '}
          <Typography>
            {formatNumber(
              creditsOrderData?.creditsPerCycle +
                creditsOrderData?.creditsRollover,
              language,
            )}{' '}
            {t('characters').toLowerCase()}
          </Typography>
        </Box>
      </Box>
    );
  };

  const ExpiryDate = () =>
    [isUIBuyTopupCharacters, packageState?.cycle, isCustomPackage]?.some(
      Boolean,
    ) && (
      <Box className="package-info-card">
        <Typography className="package-info-title">
          {isUIBuyTopupCharacters
            ? t('expiryDate')
            : t('packageExpireDuration')}
        </Typography>
        <Box className="package-info-detail">
          <TimeExpire />
        </Box>
      </Box>
    );

  const AutoRenew = () => {
    const isYearlyPackage = packageState?.expiresIn === PACK_DURATION?.YEAR;
    if (!packageState?.isAutoRenew) {
      return null;
    }

    return (
      <Box className="package-info-card">
        <Typography className="package-info-title">
          <div className="details-price-resources">
            {t('autoRenewal')}
            <Tooltip
              arrow
              placement="top"
              title={contentTooltipAutoRenew()}
              onClick={() =>
                setOpenPopupInfoTooltipMobile(contentTooltipAutoRenew)
              }
            >
              <InfoRoundedIcon fontSize="16px" className="icon-info-rounded" />
            </Tooltip>
          </div>
        </Typography>
        <Box className="package-info-detail">
          {isYearlyPackage ? t('yearly') : t('monthly')}
        </Box>
      </Box>
    );
  };

  const VoucherDiscount = () => {
    const discountPrice =
      isUIBuyTopupCharacters && isVietNam
        ? calculateCharacterPurchase?.packagePrice -
          calculateCharacterPurchase?.customPrice
        : Math.round(
            (calculateCharacterPurchase?.usdPackagePrice -
              calculateCharacterPurchase?.customUsdPrice) *
              100,
          ) / 100;

    const calculateVoucherDiscount = () => {
      const currPrice = isCustomPackage ? customPackagePrice?.price : price;
      const currUsdPrice = isCustomPackage
        ? customPackagePrice?.price
        : usdPrice;

      const finalPrice = getSavedPrice({
        voucher: voucherState,
        currPrice,
        currUsdPrice,
      });

      return finalPrice;
    };

    const voucherDiscount = calculateVoucherDiscount();
    if (!Number(voucherDiscount) && !discountPrice) return null;

    return (
      <Box className="package-info-card">
        <Typography className="package-info-title">
          {t('promotionText')}
        </Typography>
        <Typography className="hight-light-text">
          -{' '}
          {!isVietNam && (
            <span className="discount-unit-price font-size-16">$</span>
          )}
          {isVietNam && (
            <span className="discount-unit-price underline font-size-16">
              đ
            </span>
          )}
          {discountPrice
            ? formatNumber(discountPrice, language)
            : formatNumber(voucherDiscount, language)}
        </Typography>
      </Box>
    );
  };

  const PackagePrice = () => (
    <Box className="package-info-card">
      <Typography className="package-info-card-title">
        <div className="details-price-resources">
          {t('packagePrice')}
          {isCustomPackage && (
            <Tooltip
              arrow
              placement="top"
              title={
                isRolloverCredits &&
                creditsOrderData?.isShowTooltipRolloverCredits
                  ? contentTooltipResourcesRollover()
                  : contentTooltipResources()
              }
              onClick={() => setOpenPopupContentPricesResources(true)}
              maxWidth="100%"
            >
              <InfoRoundedIcon fontSize="16px" className="icon-info-rounded" />
            </Tooltip>
          )}
          {packageState?.isAutoRenew && (
            <Tooltip
              arrow
              placement="top"
              title={contentTooltipInfoPriceAutoRenew()}
              onClick={() =>
                setOpenPopupInfoTooltipMobile(contentTooltipInfoPriceAutoRenew)
              }
            >
              <InfoRoundedIcon fontSize="16px" className="icon-info-rounded" />
            </Tooltip>
          )}
        </div>
      </Typography>
      <Typography>
        {!isVietNam && (
          <span className="discount-unit-price font-size-16">$</span>
        )}
        {isVietNam && (
          <span className="discount-unit-price underline font-size-16">đ</span>
        )}
        {formatNumber(totalPrice, language)}
      </Typography>
    </Box>
  );

  const LoadingSkeleton = () => (
    <Grid container spacing={2}>
      {[...Array(4).keys()]?.map((answer) => (
        <Grid
          style={{ marginBottom: 0, padding: '0px 12px' }}
          item
          xs={12}
          sm={6}
          md={12}
          lg={12}
          xl={12}
          key={answer.key}
        >
          <div className="container-skeleton">
            <Skeleton
              animation="wave"
              height={30}
              width="20%"
              className="skeleton"
            />
            <Skeleton
              animation="wave"
              height={30}
              width="20%"
              className="skeleton"
            />
          </div>
        </Grid>
      ))}
    </Grid>
  );

  const renderTooltipRemainingValue = () => {
    const addOnDevices =
      user.maxDevices - (usingPackage?.customResource?.devices?.min || 0);
    const addOnVoiceCloning =
      user.maxCloningVoices -
      (usingPackage?.customResource?.voiceClonings?.min || 0);

    const dataTooltip = [];
    dataTooltip.push(
      `<ul style='text-align: left; margin: 0; padding: 8px 16px; font-size: 14px;'>`,
    );
    if (packageName)
      dataTooltip.push(`<li> ${t('package')}: ${packageName}</li>`);

    if (remainingCredits)
      dataTooltip.push(
        `<li> ${t('remainingCharacters')} ${remainingCredits?.toLocaleString(
          language,
        )} ${remainingCredits > 1 ? t('characters') : t('character')}</li>`,
      );

    if (remainingDate)
      dataTooltip.push(`<li> ${t('remainingDate')} ${remainingDate}</li>`);

    if (addOnDevices)
      dataTooltip.push(
        `<li> ${t('additionalDevices')}: ${addOnDevices} ${
          addOnDevices > 1 ? t('devices') : t('device')
        }</li>`,
      );

    if (addOnVoiceCloning)
      dataTooltip.push(
        `<li> ${t('additionalVoiceCloning')} ${addOnVoiceCloning} ${
          addOnVoiceCloning > 1 ? t('voices') : t('voiceTooltip')
        }</li>`,
      );
    dataTooltip.push(`</ul>`);

    return dataTooltip.join('');
  };

  const packageNameaOrder = getPackageName(
    packageState?.name,
    packageState?.code,
    language,
  );

  return (
    <StyledOrder>
      <Typography className="info-title">{t('orderInfo')}</Typography>
      {loadingGetPreviewRolloverCredits || loadingGetBanks || !packageState ? (
        <LoadingSkeleton />
      ) : (
        <>
          <Box className="package-info-card">
            <Typography className="package-info-title">
              {t('namePackage')}
            </Typography>
            <Box className="package-info-detail">
              <Box display="flex" gap="8px">
                <Typography>
                  {isUIBuyTopupCharacters ? (
                    <Typography
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(
                          language === LANGUAGE.VN
                            ? packageState?.name?.[language]
                            : packageState?.name?.[LANGUAGE.EN],
                        ),
                      }}
                      className="title-text"
                    />
                  ) : (
                    `${currentProduct || ''} - ${packageNameaOrder}`
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>

          <TotalCredits />
          {!isCustomPackage &&
          isRolloverCredits &&
          creditsOrderData?.isRolloverCredits ? (
            <RolloverCredits />
          ) : (
            <TotalCharsPerMonth />
          )}
          <ExpiryDate />
          <AutoRenew />

          <Divider />
          <Typography className="info-title">
            {t('billingInformation')}
          </Typography>
          <PackagePrice />
          <VoucherDiscount />

          {(determinePackageChange() === PACKAGE_CHANGE_TYPES.UPGRADE ||
            isUsingCustomPackage) &&
            !isUIBuyTopupCharacters &&
            remainingValueOfTheCurrentPackage?.price > 0 && (
              <>
                <Box className="package-info-card">
                  <div className="remaning-value-info">
                    <Typography className="package-info-card-title">
                      {t('eductionForOldPackage')} {isMobile && <>(*)</>}
                    </Typography>
                    {!isMobile && (
                      <Tooltip
                        arrow
                        placement="top"
                        title={
                          <div
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{
                              __html: isUsingCustomPackage
                                ? renderTooltipRemainingValue()
                                : t('descriptionRemainingValuePackage', {
                                    packageName,
                                    remainingCharacters:
                                      remainingCredits?.toLocaleString(
                                        language,
                                      ),
                                    remainingDate: remainingDate.toString(),
                                  }),
                            }}
                          />
                        }
                      >
                        <InfoRoundedIcon
                          fontSize="16px"
                          className="tooltip-remaning-value"
                        />
                      </Tooltip>
                    )}
                  </div>
                  <Typography className="hight-light-text">
                    {'- '}
                    {!isVietNam && (
                      <>
                        <span className="discount-unit-price font-size-16">
                          $
                        </span>
                        {remainingValueOfTheCurrentPackage?.usdPrice?.toLocaleString(
                          language,
                        )}
                      </>
                    )}
                    {isVietNam && (
                      <>
                        <span className="discount-unit-price underline font-size-16">
                          đ
                        </span>
                        {remainingValueOfTheCurrentPackage?.price?.toLocaleString(
                          language,
                        )}
                      </>
                    )}
                  </Typography>
                </Box>
                {isMobile && (
                  <div className="description-remaining-box">
                    <div className="details-text">{t('details')}</div>
                    <div
                      className="description-remaining-mobile"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: isUsingCustomPackage
                          ? renderTooltipRemainingValue()
                          : t('descriptionRemainingValuePackage', {
                              packageName,
                              remainingCharacters:
                                remainingCredits?.toLocaleString(language),
                              remainingDate: remainingDate.toString(),
                            }),
                      }}
                    />
                  </div>
                )}
              </>
            )}
          <PopupContentPricesResources />
          {/* <PopupTooltipInfoAutoRenew />
      <PopupTooltipInfoPriceAutoRenew /> */}
          <PopupInfoTooltipMobile />
          <Divider />
        </>
      )}
    </StyledOrder>
  );
};

export default Order;
